import { useRef, useCallback, useEffect } from 'react';
import { SSEParser } from '../services/sseParser';
import type {
  ContentPart,
} from '../components/ChatMessage';

// ---------------------------------------------------------------------------
// Event payload types (mirror backend SSE event schemas)
// ---------------------------------------------------------------------------

export interface FileMetadataEvent {
  files: Array<{
    filename: string;
    content_type: string;
    size: number;
    download_url: string;
  }>;
}

export interface ImageGenerationEvent {
  original_prompt: string;
  revised_prompt?: string;
  images: Array<{ blobName?: string; downloadUrl?: string; base64?: string; [key: string]: any }>;
}

export interface CodeExecutionEvent {
  code: string;
  language: string;
}

export interface GeneratedFilesEvent {
  files: Array<{ filename: string; size: number; downloadUrl: string; contentType?: string; blobName?: string }>;
}

export interface WebSearchResultsEvent {
  search_status: 'success' | 'redacted' | 'blocked';
  results: Array<{ title: string; url: string; content: string }>;
  original_query?: string;
  modified_query?: string;
}

export interface StreamErrorEvent {
  message: string;
}

// ---------------------------------------------------------------------------
// Stream snapshot — the structured state the hook exposes on every update
// ---------------------------------------------------------------------------

export interface StreamSnapshot {
  /** Clean text content (LLM token text only). */
  content: string;
  /** Ordered sequence of typed content parts for rendering. */
  parts: ContentPart[];
}

// ---------------------------------------------------------------------------
// Callbacks the caller supplies
// ---------------------------------------------------------------------------

export interface SSEHandlers {
  /** Called (throttled to ~60 fps) whenever the stream state changes. */
  onUpdate?: (snapshot: StreamSnapshot) => void;
  onFileMetadata?: (data: FileMetadataEvent) => void;
  onFileProcessing?: (isAnalyzing: boolean) => void;
  onImageGeneration?: (data: ImageGenerationEvent) => void;
  onError?: (data: StreamErrorEvent) => void;
  onDone?: (snapshot: StreamSnapshot) => void;
}

// ---------------------------------------------------------------------------
// Safe error messages — never leak server internals to the user
// ---------------------------------------------------------------------------

function getSafeErrorMessage(status: number): string {
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 400 && status < 500) return 'There was a problem with your request. Please try again.';
  return 'Something went wrong. Please try again later.';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useStreamingChat() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  /** Abort an in-progress stream. Safe to call when idle. */
  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  /**
   * Stream from a backend SSE endpoint.
   *
   * @returns The final {@link StreamSnapshot} once the stream ends.
   * @throws  On network errors or non-2xx HTTP responses.
   */
  const streamChat = useCallback(async (
    url: string,
    init: RequestInit,
    handlers: SSEHandlers,
  ): Promise<StreamSnapshot> => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const response = await fetch(url, { ...init, signal: controller.signal });

    if (!response.ok) {
      const safeMessage = getSafeErrorMessage(response.status);
      handlers.onError?.({ message: safeMessage });
      throw new Error(safeMessage);
    }

    if (!response.body) throw new Error('No response body');

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = new SSEParser();

    // ── Mutable streaming state ────────────────────────────────────
    let textContent = '';
    const parts: ContentPart[] = [];

    const getSnapshot = (): StreamSnapshot => ({
      content: textContent,
      parts: [...parts],
    });

    const appendText = (text: string) => {
      textContent += text;
      const last = parts[parts.length - 1];
      if (last && last.type === 'text') {
        last.content += text;
      } else {
        parts.push({ type: 'text', content: text });
      }
    };

    // ── requestAnimationFrame throttling ───────────────────────────
    let pendingUpdate = false;

    const flushUpdate = () => {
      if (pendingUpdate) {
        handlers.onUpdate?.(getSnapshot());
        pendingUpdate = false;
      }
      rafIdRef.current = null;
    };

    const scheduleUpdate = () => {
      pendingUpdate = true;
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(flushUpdate);
      }
    };

    const flushImmediate = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (pendingUpdate) {
        handlers.onUpdate?.(getSnapshot());
        pendingUpdate = false;
      }
    };

    // ── Event handler map ──────────────────────────────────────────
    // Each SSE event type maps to a handler. New event types can be
    // added here without touching the read loop. Unknown types are
    // silently ignored.

    const eventHandlers: Record<string, (data: any) => void> = {
      token:              (d) => { appendText(d.content ?? ''); scheduleUpdate(); },
      web_search_results: (d) => { parts.push({ type: 'web_search_results', search_status: d.search_status, results: d.results, original_query: d.original_query, modified_query: d.modified_query }); scheduleUpdate(); },
      code_execution:     (d) => { parts.push({ type: 'code_execution', code: d.code, language: d.language }); scheduleUpdate(); },
      generated_files:    (d) => { parts.push({ type: 'generated_files', files: d.files }); scheduleUpdate(); },
      image_generation:   (d) => { parts.push({ type: 'image_generation', original_prompt: d.original_prompt, revised_prompt: d.revised_prompt, images: d.images }); handlers.onImageGeneration?.(d as ImageGenerationEvent); scheduleUpdate(); },
      file_metadata:      (d) => handlers.onFileMetadata?.(d as FileMetadataEvent),
      // Snowflake agent structured events
      snowflake_thinking:    (d) => { parts.push({ type: 'snowflake_thinking', text: d.text }); scheduleUpdate(); },
      snowflake_chart:       (d) => { parts.push({ type: 'snowflake_chart', spec: d.spec }); scheduleUpdate(); },
      snowflake_table:       (d) => { parts.push({ type: 'snowflake_table', ...d }); scheduleUpdate(); },
      snowflake_status:      (d) => { parts.push({ type: 'snowflake_status', message: d.message }); scheduleUpdate(); },
      snowflake_tool_status: (d) => { parts.push({ type: 'snowflake_tool_status', message: d.message }); scheduleUpdate(); },
      file_processing:    (d) => { handlers.onFileProcessing?.(d.status !== 'done'); },
      error:              (d) => handlers.onError?.(d as StreamErrorEvent),
      done:               ()  => { flushImmediate(); handlers.onDone?.(getSnapshot()); },
    };

    // ── Read loop ──────────────────────────────────────────────────
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const events = parser.feed(chunk);

        for (const event of events) {
          eventHandlers[event.type]?.(event.data);
        }
      }

      flushImmediate();
    } finally {
      reader.releaseLock();
    }

    return getSnapshot();
  }, []);

  return { streamChat, abort };
}